import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Calendar, Clock, User, Building2, Mail, Phone, MessageSquare, 
  RefreshCw, Send, X, ChevronLeft, ChevronRight, Eye, CheckCircle,
  AlertCircle, ExternalLink, Copy
} from 'lucide-react';

import { EmailComposerModal } from '../components/EmailComposerModal';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function BookingsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, calendar
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState('upcoming'); // all, upcoming, past
  const [showBookingEmailModal, setShowBookingEmailModal] = useState(false);
  const [bookingEmailRecipient, setBookingEmailRecipient] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, availabilityRes] = await Promise.all([
        api.get('/api/bookings/my'),
        api.get('/api/staff/availability')
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setAvailability(availabilityRes.data.availability);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (bookingId) => {
    try {
      await api.put(`/api/bookings/${bookingId}/resend`);
      toast.success('Reminder email sent');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await api.put(`/api/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const copyBookingLink = () => {
    if (availability?.slug) {
      const link = `${window.location.origin}/booking/${availability.slug}`;
      navigator.clipboard.writeText(link);
      toast.success('Booking link copied!');
    }
  };

  const filteredBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (filter === 'upcoming') return bookingDate >= today && b.status !== 'cancelled';
    if (filter === 'past') return bookingDate < today;
    return true;
  });

  const getBookingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.date === dateStr && b.status !== 'cancelled');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Calendar navigation
  const goToPrevMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#014DB7]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bookings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">My Bookings</h1>
          <p className="text-gray-500">View and manage your scheduled meetings</p>
        </div>
        <div className="flex items-center gap-3">
          {availability?.slug && (
            <Button variant="outline" onClick={copyBookingLink} className="gap-2">
              <Copy className="w-4 h-4" />
              Copy Booking Link
            </Button>
          )}
          <a
            href={availability?.slug ? `/booking/${availability.slug}` : '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#84CC16] hover:bg-[#65a30d] text-white rounded-xl font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View Public Page
          </a>
        </div>
      </div>

      {/* My Booking URL */}
      {availability && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Your Booking Page URL</p>
              <p className="text-blue-600 font-mono text-sm mt-1">
                {window.location.origin}/booking/{availability.slug}
              </p>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                availability.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {availability.is_active ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                {availability.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle & Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-white text-[#0A1628] shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            List View
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              viewMode === 'calendar' ? 'bg-white text-[#0A1628] shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Calendar
          </button>
        </div>

        {viewMode === 'list' && (
          <div className="flex items-center gap-2">
            {['upcoming', 'past', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                  filter === f ? 'bg-[#014DB7] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {filteredBookings.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">No Bookings</h3>
              <p className="text-gray-500">
                {filter === 'upcoming' ? 'No upcoming meetings scheduled' : 'No bookings found'}
              </p>
            </div>
          ) : (
            filteredBookings.map((booking) => (
              <div
                key={booking.id}
                className={`bg-white rounded-xl border p-4 transition-colors ${
                  booking.status === 'cancelled' 
                    ? 'border-red-200 bg-red-50/50' 
                    : 'border-gray-200 hover:border-[#014DB7]/30'
                }`}
                data-testid={`booking-${booking.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-[#014DB7] rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0">
                    <span className="text-xs uppercase">
                      {new Date(booking.date).toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="text-2xl font-bold">
                      {new Date(booking.date).getDate()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#0A1628]">{booking.booker_name}</h3>
                      {booking.is_existing_client && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Client</span>
                      )}
                      {booking.status === 'cancelled' && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Cancelled</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {booking.start_time} - {booking.end_time}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {booking.booker_company}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-1">
                      <MessageSquare className="w-4 h-4 inline mr-1" />
                      {booking.reason_for_meeting}
                    </p>
                  </div>
                  {booking.status !== 'cancelled' && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => setSelectedBooking(booking)}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleResend(booking.id)} title="Send reminder email">
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleCancel(booking.id)} className="text-red-600 hover:bg-red-50">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <button onClick={goToPrevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-[#0A1628]">
              {MONTHS[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </h3>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
            ))}
            {getDaysInMonth(selectedDate).map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} className="h-24 bg-gray-50 rounded-lg" />;
              const dayBookings = getBookingsForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div
                  key={day.toISOString()}
                  className={`h-24 p-1 rounded-lg border ${
                    isToday ? 'border-[#014DB7] bg-blue-50/50' : 'border-gray-100 hover:bg-gray-50'
                  }`}
                >
                  <div className={`text-sm font-medium mb-1 ${isToday ? 'text-[#014DB7]' : 'text-gray-700'}`}>{day.getDate()}</div>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayBookings.slice(0, 2).map(b => (
                      <div
                        key={b.id}
                        onClick={() => setSelectedBooking(b)}
                        className="text-xs bg-[#014DB7] text-white px-1 py-0.5 rounded truncate cursor-pointer hover:bg-blue-700"
                      >
                        {b.start_time} {b.booker_name}
                      </div>
                    ))}
                    {dayBookings.length > 2 && <div className="text-xs text-gray-500">+{dayBookings.length - 2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0A1628]">Booking Details</h3>
              <button onClick={() => setSelectedBooking(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-[#014DB7] text-white rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-200 text-sm">Date & Time</p>
                    <p className="text-xl font-bold">{formatDate(selectedBooking.date)}</p>
                    <p className="text-lg">{selectedBooking.start_time} - {selectedBooking.end_time}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-200 text-sm">Duration</p>
                    <p className="text-2xl font-bold">{selectedBooking.duration}m</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Name</p>
                  <p className="font-medium flex items-center gap-2"><User className="w-4 h-4 text-gray-400" />{selectedBooking.booker_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Company</p>
                  <p className="font-medium flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" />{selectedBooking.booker_company}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <button onClick={() => { setBookingEmailRecipient({ email: selectedBooking.booker_email, name: selectedBooking.booker_name }); setShowBookingEmailModal(true); }} className="font-medium text-[#014DB7] flex items-center gap-2 hover:underline">
                    <Mail className="w-4 h-4" />{selectedBooking.booker_email}
                  </button>
                </div>
                {selectedBooking.booker_phone && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Phone</p>
                    <a href={`tel:${selectedBooking.booker_phone}`} className="font-medium text-[#014DB7] flex items-center gap-2">
                      <Phone className="w-4 h-4" />{selectedBooking.booker_phone}
                    </a>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Reason for Meeting</p>
                <p className="bg-gray-50 rounded-lg p-3 text-gray-700">{selectedBooking.reason_for_meeting}</p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className={`px-2 py-1 rounded-full ${selectedBooking.is_existing_client ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {selectedBooking.is_existing_client ? 'Existing Client' : 'New Lead'}
                </span>
                <span className={`px-2 py-1 rounded-full capitalize ${
                  selectedBooking.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                  selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}>{selectedBooking.status}</span>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => { handleResend(selectedBooking.id); setSelectedBooking(null); }}>
                  <Send className="w-4 h-4 mr-2" /> Send Reminder
                </Button>
                {selectedBooking.status !== 'cancelled' && (
                  <Button variant="outline" className="flex-1 text-red-600 hover:bg-red-50" onClick={() => { handleCancel(selectedBooking.id); setSelectedBooking(null); }}>
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <EmailComposerModal
        isOpen={showBookingEmailModal}
        onClose={() => { setShowBookingEmailModal(false); setBookingEmailRecipient(null); }}
        recipientEmail={bookingEmailRecipient?.email}
        recipientName={bookingEmailRecipient?.name}
      />
    </div>
  );
}
